import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Avatar, usePortraitImg } from '../../components/Avatar';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import { occasionIcon, occasionText } from '../../components/Occasions';
import { coverOf } from '../../domain/covers';
import { formatFuzzyDate, lifeYears } from '../../domain/dates';
import { isAudio, isOwnDay, isVideo, memoriesFor, sameAnchor } from '../../domain/milestones';
import { ageInDays, freshScale, FRESH_DAYS, FRESH_PEAK } from '../../domain/memoryDots';
import { contributionSpeaker, contributionStory, momentPeople, momentUploader, storyPersonPerson, storyTeller } from '../../domain/piles';
import { nextOccasionFor, occasionDayLabel } from '../../domain/occasions';
import { displayName, givenName } from '../../domain/names';
import { circlePortraitRight } from '../../domain/ownership';
import { recipeCover, recipeWhen, recipesOnCircle } from '../../domain/recipes';
import type { FamilyPhoto, MemoryAnchor, MemorySubject, PersonPortrait, PersonWithNames, Recipe, Story, UUID } from '../../domain/types';
import { useI18n } from '../../i18n';
import { byId, groupedBy, groupedByEach, pileOf } from '../../lib/grouped';
import { preparePortrait } from '../../lib/image';
import { formatDuration, relativeTime } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';
import { useApp, type NodeOrigin } from '../../app/AppState';
import { useNavigate } from '../../app/router';
import { usePortraitUpload } from '../people/PortraitPicker';
import { ReachButtons } from '../people/Reach';
import { MILESTONE_ICONS, MILESTONE_TONES, type MilestoneTone } from './milestoneIcons';
import { MomentDialog, PhotoImage } from './MomentDialog';
import { AddMemories } from './MemoryGrid';
import { VoiceNoteDialog } from './VoiceNote';

/** Long enough to read as a movement rather than a cut; short enough that nobody waits for it. */
const FLIGHT_MS = 420;
/**
 * Going back is quicker than coming in, on purpose.
 *
 * Arriving has something to show you: the circle grows, the ring arrives around
 * it, and the 420ms is what makes that read as one object travelling rather than
 * a screen swap. Leaving has nothing to show — you already know where you are
 * going — so the journey only has to land.
 */
const LEAVE_MS = 300;
/** Press and hold: long enough not to fire on a tap, short enough not to feel broken. */
const HOLD_MS = 500;

/**
 * One day the family remembers, as a constellation.
 *
 * Tapping a line on the timeline sends its node to the middle of the screen and
 * takes everything else away: what is left is that day and, floating around it
 * on drawn lines, everything the family has put inside it — photographs, clips,
 * and recordings of people saying what they remember. The plus at the foot adds
 * another one, and every new thing arrives as another node on the ring. After a
 * wedding this is where the family empties their phones.
 *
 * Three things make it feel like one object rather than two screens:
 *
 *   - **it grows out of where it was.** The caller measures the little node it
 *     tapped and hands the rect over as `origin`; the big node is first drawn
 *     transformed onto that rect and then released, so the movement is the same
 *     circle travelling rather than one screen replacing another. Back reverses
 *     it exactly, and because this is an overlay the timeline underneath never
 *     moved — you land on the same line you left.
 *   - **everything inside is the same kind of record.** A picture, a clip and a
 *     voice note are all Moments naming this day through `addedTo`, so they all
 *     come from `memoriesFor`, they all open in the same dialog, and they are
 *     all findable afterwards in the Moments tab and under the circle of
 *     whoever added them.
 *   - **the day itself stays reachable.** The middle node is a button: a story
 *     opens its recordings, a birth opens that person's card.
 */
export function MemoryNodes({ subject, origin, onClose }: { subject: MemorySubject; origin: NodeOrigin | null; onClose: () => void }) {
  const { t, lang } = useI18n();
  const { moments, session, reducedMotionActive, openStory, openProfile, openMemories, openRecipe, openRecipeEditor, treeSettings, actor, links, grants, peopleById, memoryDepth, tapOutsideBack } = useApp();
  /** A recipe is filled like a day, but it is not one — a few things here are said differently over it. */
  const isRecipe = subject.kind === 'recipe';
  const navigate = useNavigate();
  /** A day can be added to; a person is a life, and the screen reads a little differently. */
  const anchor: MemoryAnchor | null = subject.kind === 'person' ? null : subject;

  const [openItem, setOpenItem] = useState<FamilyPhoto | null>(null);
  const [openPortrait, setOpenPortrait] = useState<PersonPortrait | null>(null);
  const [adding, setAdding] = useState(false);
  const [recording, setRecording] = useState(false);
  /**
   * One picture the viewer is holding down, offered as the face on the tree.
   * Null the rest of the time — see the note on the gesture below.
   */
  const [pickFrom, setPickFrom] = useState<RingNode | null>(null);
  /*
   * Measured off the window rather than off an element: this is rendered inside
   * a <dialog> that is still `display: none` when the first layout effect runs
   * (the dialog is only shown a moment later), so every element in here is 0×0
   * at exactly the moment the flight has to be worked out. The window is always
   * there, and the middle of the constellation is a fixed fraction of it — the
   * same fraction the element is positioned by, below.
   */
  const [space, setSpace] = useState(measure);
  /*
   * Where the screen is over the constellation. Nothing is ever laid out
   * smaller to fit; with a lot of nodes the constellation is simply bigger than
   * the screen, and this is how you get to the rest of it — drag to move, pinch
   * or wheel to pull back. It starts at 1:1 on the middle node.
   */
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const pinch = useRef<Map<number, { x: number; y: number }>>(new Map());
  /** Where the two fingers and the view were when the pinch began — every frame is measured from here. */
  const pinchStart = useRef<{ d: number; k: number; x: number; y: number } | null>(null);
  const closing = useRef(false);

  const items = useMemo(() => memoriesFor(moments, subject), [moments, subject]);
  /* Everything that goes on the ring, in one list: pictures, and — for a person — their stories and their circle photos too. */
  const ringItems = useRingItems(subject, items);
  const head = useHead(subject);

  /**
   * The big node, drawn sitting exactly on the little one that was tapped. It
   * is computed during the first render, so the very first painted frame is
   * already in the right place and the transition has two real states to move
   * between. Without an origin — a notification's link, say — there is nowhere
   * for it to have come from, so it simply appears.
   */
  const flightTo = (v: { x: number; y: number; k: number }) => {
    if (!origin || reducedMotionActive) return null;
    // The hub's transform is applied *inside* the panned and zoomed space, so the
    // journey is measured in that space: undo the pan, then divide by the zoom.
    const cx = space.w * CENTRE_X + v.x;
    const cy = space.hFull * CENTRE_Y + v.y;
    return `translate(${Math.round((origin.x - cx) / v.k)}px, ${Math.round((origin.y - cy) / v.k)}px) scale(${(origin.size / (space.hub * v.k)).toFixed(3)})`;
  };
  const flight = useMemo(() => flightTo({ x: 0, y: 0, k: 1 }), [origin, space, reducedMotionActive]);

  /** 'in' and 'out' are the flight; 'open' is the constellation at rest. */
  const [phase, setPhase] = useState<'in' | 'open' | 'out'>(origin && !reducedMotionActive ? 'in' : 'open');
  useEffect(() => {
    if (phase !== 'in') return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setPhase('open'));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
    // Once, on the way in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The ring is laid out against the room it has, so it has to be told when that changes.
  useEffect(() => {
    const onResize = () => setSpace(measure());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const close = () => {
    if (closing.current) return;
    closing.current = true;
    if (!flight || reducedMotionActive) return onClose();
    setOutFlight(flightTo(live.current));
    setPhase('out');
    window.setTimeout(onClose, LEAVE_MS);
  };
  /** Where the node has to fly back to, worked out from wherever the screen has been moved to. */
  const [outFlight, setOutFlight] = useState<string | null>(null);

  /* ---- moving the screen over the constellation ---- */
  /*
   * **The gesture is written straight onto the element, not through React.**
   *
   * A finger fires a pointermove per frame or more, and a constellation can
   * hold hundreds of nodes: routing each one through `setState` re-rendered the
   * whole ring per event, which is exactly what made pinching flicker and
   * dragging feel like it was catching. So `live` is the truth while a hand is
   * down — one transform written on one element, the compositor moving every
   * child for free — and React is told once, on release, because the flight
   * home and the "back to the middle" button need to know where we ended up.
   *
   * The pinch is computed from where the fingers *started*, never from the last
   * frame: reading the current scale back out and multiplying by a delta
   * accumulates rounding on every event, which is the other half of the
   * flicker.
   */
  const live = useRef({ ...view });
  const spaceRef = useRef<HTMLDivElement | null>(null);
  const raf = useRef(0);
  /*
   * **Only what is near the screen exists.**
   *
   * Nothing is ever laid out smaller to fit, so a life's worth of pictures is a
   * constellation many screens across — and it used to be *all there*, every
   * circle a button with a picture in it, a line drawn to each, whether or not
   * the screen was anywhere near it. A thousand of those is what makes a phone
   * slow to open the circle, slow to drag it, and eventually out of memory.
   *
   * So the ring is mounted through a window: `around` is a box in the
   * constellation's own coordinates, the screen plus half a screen on every
   * side, and a node outside it is simply not rendered (nor is its line, nor is
   * its picture asked for). The box is checked once a frame while a hand is
   * moving the screen, and replaced only when the screen has come within a
   * node's width of its edge — which is the one time React is told anything
   * during a gesture, and it mounts a strip of new nodes rather than redrawing
   * the ring: every node is memoised and the ones already there are untouched.
   * A day with forty pictures in it never notices any of this.
   */
  const spaceNow = useRef(space);
  spaceNow.current = space;
  const [around, setAround] = useState<Box>(() => grown(seenBox(space, view), NODE_REACH));
  const lookAround = useCallback(() => {
    const seen = seenBox(spaceNow.current, live.current);
    // The biggest a node is ever drawn: nothing may come on screen that is not already there.
    const reach = NODE_REACH / live.current.k;
    const ideal = grown(seen, reach);
    const area = (b: Box) => (b.x1 - b.x0) * (b.y1 - b.y0);
    setAround((was) => {
      const covers = seen.x0 - reach >= was.x0 && seen.y0 - reach >= was.y0 && seen.x1 + reach <= was.x1 && seen.y1 + reach <= was.y1;
      // Still covers the screen — and is not the great box left over from having pulled right back, which would
      // keep a thousand circles alive behind the twenty somebody has leaned back in on.
      return covers && area(was) <= area(ideal) * 4 ? was : ideal;
    });
  }, []);
  const paint = () => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const el = spaceRef.current;
      if (!el) return;
      const v = live.current;
      el.style.transform = `translate(${Math.round(v.x)}px, ${Math.round(v.y)}px) scale(${v.k.toFixed(3)})`;
      lookAround();
    });
  };
  useEffect(
    () => () => {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
    },
    [],
  );
  // A turned phone, a resized window: the screen now shows a different part of the ring.
  useEffect(lookAround, [space, lookAround]);
  /** What the screen has been moved to, whether or not React has caught up. */
  const [moved, setMoved] = useState(false);
  const settle = () => {
    const v = live.current;
    setMoved(v.k !== 1 || v.x !== 0 || v.y !== 0);
    setView({ ...v });
    lookAround();
  };
  /*
   * A wheel has no "let go", so it settles when it has been quiet for a moment —
   * telling React on every notch of it re-rendered this whole screen twenty
   * times a second for the length of a scroll.
   */
  const quiet = useRef<number | null>(null);
  const settleSoon = () => {
    if (quiet.current !== null) window.clearTimeout(quiet.current);
    quiet.current = window.setTimeout(() => {
      quiet.current = null;
      settle();
    }, 140);
  };
  useEffect(() => () => {
    if (quiet.current !== null) window.clearTimeout(quiet.current);
  }, []);

  const zoomTo = (k: number, px: number, py: number, fromScale: number, fromX: number, fromY: number) => {
    const next = Math.min(1.6, Math.max(0.3, k));
    // Keep whatever is under the fingers under the fingers.
    const cx = space.w * CENTRE_X;
    const cy = space.hFull * CENTRE_Y;
    const s = next / fromScale;
    live.current = { k: next, x: px - cx - (px - cx - fromX) * s, y: py - cy - (py - cy - fromY) * s };
    paint();
  };
  const onPointerDown = (e: ReactPointerEvent) => {
    // A new gesture: whatever the last one was, it is over and its click has been and gone.
    dragged.current = false;
    pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current.size === 2) {
      const [a, b] = [...pinch.current.values()];
      pinchStart.current = { d: Math.hypot(a.x - b.x, a.y - b.y), ...live.current };
      // Two fingers is a pinch, and a pinch that began as a drag must not also pan.
      if (drag.current?.moved) dragged.current = true;
      drag.current = null;
      // Nor is it a press and hold, whatever it started out as.
      cancelPress();
      return;
    }
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (pinch.current.has(e.pointerId)) pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const start = pinchStart.current;
    if (pinch.current.size === 2 && start) {
      const [a, b] = [...pinch.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (start.d > 0) {
        dragged.current = true;
        // Absolute, from the start of the pinch: no drift however many events arrive.
        zoomTo((start.k * d) / start.d, (a.x + b.x) / 2, (a.y + b.y) / 2, start.k, start.x, start.y);
      }
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    // Moving the screen is not holding a picture still.
    cancelPress();
    d.moved = true;
    d.x = e.clientX;
    d.y = e.clientY;
    live.current = { ...live.current, x: live.current.x + dx, y: live.current.y + dy };
    paint();
  };
  const endPointer = (e: ReactPointerEvent) => {
    cancelPress();
    pinch.current.delete(e.pointerId);
    if (pinch.current.size < 2 && pinchStart.current) {
      pinchStart.current = null;
      settle();
    }
    if (drag.current?.id === e.pointerId) {
      // A drag is not a tap: swallow the click the browser is about to send to whatever is under it.
      if (drag.current.moved) {
        dragged.current = true;
        settle();
      }
      drag.current = null;
    }
  };
  /**
   * True only between the end of a drag and the click that drag would
   * otherwise have triggered. It used to be cleared *by* that click — so a
   * drag the browser sent no click after (ending on the background, or on a
   * second finger) left the flag raised and ate the next real tap on a
   * picture. It is cleared at the start of every gesture now instead.
   */
  const dragged = useRef(false);

  /* ---- press and hold a picture ---- */
  /*
   * The camera on the rim of the middle node takes a *new* photograph. This is
   * the other half of the same thought: the right picture is very often already
   * on the ring — the family put it there — so holding it down offers it as the
   * face on the tree.
   *
   * It has to share the screen with a pan and a pinch, so the timer is cancelled
   * by anything that turns out to be a gesture rather than a hold: a second
   * finger, six pixels of movement, or the finger coming up early. And when it
   * does fire, `dragged` is raised so that `onClickCapture` swallows the click
   * the press is about to become — otherwise the picture would open behind the
   * sheet that just asked about it.
   */
  const press = useRef<number | null>(null);
  /*
   * These four are handed to every node on the ring, and a node is memoised —
   * so they keep one identity for the life of the screen, reading whatever
   * changes (whether this face is the viewer's to change) through a ref. An
   * arrow written afresh on each render would re-render every node on every
   * render, which is the very thing the memo is there to stop.
   */
  const cancelPress = useCallback(() => {
    if (press.current !== null) window.clearTimeout(press.current);
    press.current = null;
  }, []);
  useEffect(() => cancelPress, [cancelPress]);
  const mayHold = useRef(false);
  const holdable = (node: RingNode) =>
    mayHold.current && (node.kind === 'portrait' || (node.kind === 'photo' && !!node.photo.mimeType?.startsWith('image/')));
  const startPress = useCallback((node: RingNode) => {
    cancelPress();
    if (!holdable(node)) return;
    press.current = window.setTimeout(() => {
      press.current = null;
      dragged.current = true;
      setPickFrom(node);
    }, HOLD_MS);
  }, [cancelPress]);
  /** Right-click does what holding does, for anyone with a mouse. */
  const offerAsFace = useCallback((node: RingNode, e: ReactMouseEvent) => {
    if (!holdable(node)) return;
    e.preventDefault();
    setPickFrom(node);
  }, []);
  const openNode = useCallback(
    (node: RingNode, el: HTMLElement) => {
      if (node.kind === 'photo') setOpenItem(node.photo);
      else if (node.kind === 'portrait') setOpenPortrait(node.portrait);
      else if (node.kind === 'day') {
        // The little node grows into the next constellation, the same way this one grew out of the tree.
        const r = el.getBoundingClientRect();
        openMemories({ kind: 'moment', id: node.day.id }, { x: r.left + r.width / 2, y: r.top + r.height / 2, size: r.width });
      } else if (node.kind === 'recipe') {
        // One still being written goes back to the editor; one that is out opens to be cooked from.
        if (node.recipe.status === 'draft') openRecipeEditor({ recipeId: node.recipe.id });
        else openRecipe(node.recipe.id);
      } else openStory(node.story.id, { intent: node.hasVoice ? 'play' : undefined });
    },
    [openMemories, openRecipeEditor, openRecipe, openStory],
  );

  /** True for the moment it takes the screen to glide back to the middle, so only that move is animated. */
  const [gliding, setGliding] = useState(false);
  const recentre = () => {
    const from = live.current;
    const home = { x: 0, y: 0, k: 1 };
    /*
     * The glide home is only a glide when home is close. From a couple of
     * screens away it would be a third of a second of blur across circles that
     * would all have to exist for it — so from far off the screen simply goes
     * home. From nearby, what is on screen now *and* what is at home are both
     * kept for the length of the glide (the window would otherwise move at once,
     * and the glide would cross empty sky), and trimmed when it lands.
     */
    const nearby = Math.abs(from.x) < space.w * 1.5 && Math.abs(from.y) < space.hFull * 1.5 && from.k > 0.6;
    if (nearby) {
      const there = grown(seenBox(space, home), NODE_REACH);
      setAround((was) => ({ x0: Math.min(was.x0, there.x0), y0: Math.min(was.y0, there.y0), x1: Math.max(was.x1, there.x1), y1: Math.max(was.y1, there.y1) }));
      setGliding(true);
      window.setTimeout(() => {
        setGliding(false);
        lookAround();
      }, 340);
    }
    live.current = home;
    paint();
    setMoved(false);
    setView(home);
    if (!nearby) lookAround();
  };

  /** Every one of them, full size, however many there are. */
  const isPerson = subject.kind === 'person';
  /*
   * Whether the face on this circle is this viewer's to change — and *why*, so
   * that a control they only have because Admin mode is on is painted in the
   * admin colour, the way every other power in the app is.
   *
   * `circlePortraitRight`: your own circle, one you look after, or one nobody
   * has taken on **and that has no face yet**. It is not enough to be "not
   * somebody else's" — a camera appeared on sixteen ancestors Jay had nothing
   * to do with, which is a stranger's hand on the family's photographs.
   */
  const subjectPerson = isPerson ? peopleById.get(subject.id) : undefined;
  const photoRight = subjectPerson && actor ? circlePortraitRight(actor, links, grants, subjectPerson) : 'none';
  const mayChangePortrait = photoRight !== 'none';
  mayHold.current = mayChangePortrait;
  /** Why there is a mark on their circle in the tree — the heart, the cake, the candles. */
  const marks = useMarks(subject);
  /*
   * One clock for the whole screen, taken when it opens: every node's size is
   * measured against the same "now", and it does not creep while you look.
   */
  const now = useMemo(() => Date.now(), [subject]);
  const fadeDays = treeSettings.badgeFadeDays;
  const sizes = useMemo(() => ringItems.map((n, i) => nodeSize(n, i, isPerson, now, fadeDays)), [ringItems, isPerson, now, fadeDays]);
  const ring = useMemo(() => layoutRings(sizes, space.hub), [sizes, space.hub]);
  /*
   * A circle can hold a thousand pictures, and every one of them is a blob read
   * out of the browser's storage and an image to decode. A node that is not
   * near the screen is not mounted at all (see `around`, above), so it asks for
   * nothing; a small ring is simply all there, as it always was.
   */
  const windowed = ringItems.length > WINDOW_FROM;
  const isNear = (i: number) => {
    if (!windowed) return true;
    const at = ring[i];
    const half = sizes[i] / 2;
    return at.x + half >= around.x0 && at.x - half <= around.x1 && at.y + half >= around.y0 && at.y - half <= around.y1;
  };
  // The one place the middle of the constellation is decided — CSS reads it back out of these.
  const spaceStyle = {
    left: `${CENTRE_X * 100}%`,
    top: `${CENTRE_Y * 100}%`,
    '--hub': `${space.hub}px`,
    transform: `translate(${Math.round(view.x)}px, ${Math.round(view.y)}px) scale(${view.k.toFixed(3)})`,
  } as CSSProperties;

  if (!head) return null;

  const flying = phase !== 'open';
  /*
   * **Going back hands the screen underneath straight back to you.**
   *
   * The dim used to stay until the very end: the circle flew home, and only
   * then — when this overlay unmounted — did the tree reappear. Jay: *"first the
   * person node shifts back to its position, then there's a short delay and then
   * all the other nodes appear. I want this to feel snappier."* He is right, and
   * it is the wrong way round: the travelling circle is the *slow* part, and it
   * was gating the fast part behind it.
   *
   * So the moment Back is pressed the dim and the blur come off — the tree, or
   * the globe, or the timeline is fully there again in a breath — and the circle
   * makes its way home *over* it, landing exactly on the little one it grew out
   * of. Nothing behind is clickable while that happens (the layer stops taking
   * pointers and the dialog is still modal), which is what keeps a stray tap
   * during the landing from opening the wrong person.
   *
   * Only at the last level. A day nested inside a person's circle has another
   * constellation underneath, not the tree, and lifting the dim there would
   * flash the tree between the two.
   */
  const leaving = phase === 'out' && memoryDepth <= 1;
  const hubStyle = { transform: phase === 'out' ? outFlight ?? undefined : phase === 'in' ? flight ?? undefined : 'none', transitionDuration: `${phase === 'out' ? LEAVE_MS : FLIGHT_MS}ms` };

  return (
    <>
      <div
        className={`nodes-layer ${leaving ? 'is-leaving' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={(e) => {
          const v = live.current;
          zoomTo(v.k * (e.deltaY > 0 ? 0.9 : 1.1), e.clientX, e.clientY, v.k, v.x, v.y);
          settleSoon();
        }}
        onClickCapture={(e) => {
          if (dragged.current) {
            dragged.current = false;
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          /*
           * A tap on the sky is the way back.
           *
           * Jay: *"if you click outside of any node, just a single tap outside
           * of any node, it should take you back without having to click the
           * back button."* The ring is a handful of circles on a screenful of
           * empty space, so the empty space is by far the biggest target there
           * is — and pressing it means the same thing the corner means.
           *
           * Three things keep it from firing when it was not meant: a pan or a
           * pinch has already raised `dragged` above and returned; anything
           * that *is* part of the screen — a node, the hub, the bar, the marks,
           * the plus, the corner, the recentre button — is excluded by name
           * rather than by guesswork; and the whole thing is off when the
           * setting is off. Dialogs — the add sheet, a picture opened full
           * size — are rendered outside this layer, so a tap inside one never
           * reaches here at all. (The layer *itself* sits inside a <dialog>,
           * which is why that cannot be one of the names excluded here.)
           */
          if (!tapOutsideBack || flying) return;
          const el = e.target as HTMLElement | null;
          if (el?.closest('.nodes-sub, .nodes-hub-slot, .nodes-bar, .nodes-marks, .nodes-home, .nodes-add, .nodes-back, button, a')) return;
          e.preventDefault();
          e.stopPropagation();
          close();
        }}
      >
        {/*
          * One bar across the top: whose this is, then how to reach them.
          *
          * The far end used to hold a grid button that opened the whole day as a
          * flat list of tiles. It went because it was the same pictures again in
          * a worse shape — and because it was in the one place that needed a
          * button people would actually press: **Details**, which had been a
          * pill floating under the back button. It is an icon here rather than a
          * word, because the row already carries a name and the ways to reach
          * them, and a third word breaks the line on a phone.
          *
          * The name starts at the start of the row and stays there.
          *
          * Jay: *"the name is on the top center, but that seems to get pushed
          * around by the social media buttons that gets spawned … it gets pushed
          * more and more to the left. Maybe … left aligned on the top of the
          * page."* He is right, and the reason is worth keeping: the name used
          * to be centred between two phantom spacers, so a person with three
          * profiles and a phone number had their name in a different place from
          * a person with none — the one word on the screen that says where you
          * are moved depending on who you were looking at. Anchored to the
          * start, it is in the same place in every constellation, and the
          * buttons grow away from it rather than through it.
          */}
        <div className={`nodes-bar ${flying ? 'is-flying' : ''}`}>
          <div className="nodes-caption">
            <h2 className="nodes-title">{head.title}</h2>
            {/* What it is, or the years — never a tally; the sizes of the circles say the rest. */}
            {(head.kind || head.when) && <p className="nodes-when">{[head.kind, head.when].filter(Boolean).join(' · ')}</p>}
          </div>
          {/*
            * Who they are, in words: the one thing a constellation cannot show,
            * and the only way the card opens — on a person's circle and on the
            * day they were born alike. Nothing throws it over the screen by
            * itself.
            */}
          {subject.kind === 'person' || subject.kind === 'birth' ? (
            <span className="nodes-acts">
              {/*
                * And the ways to reach them, beside it.
                *
                * Jay: *"beside that button a bunch of social media buttons —
                * specifically WhatsApp, so if someone clicks it they're able to
                * message that individual right away … and maybe also an
                * Instagram button and a Facebook button … they'll only appear if
                * the user added the details."*
                *
                * Which is what `ReachButtons` already is, so it is the same
                * component the card and the profile carry, with the same rule: a
                * living circle that is not your own, sharing switched on, and one
                * button per detail that person actually typed. Most circles show
                * none of them, and the row is the bare Details icon it was.
                */}
              {head.person && <ReachButtons person={head.person} className="nodes-reach" />}
              {/*
                * Details opens the **profile**, not the little card.
                *
                * Jay: *"when you click it, it brings a panel that's sort of
                * useless — can we just open my profile right away, because that
                * has more details."* He is right: the card is a peek meant for
                * the tree, where you tap a circle you were not looking for and
                * want to know who it is in two lines. By the time you are inside
                * somebody's constellation you already know who they are, so the
                * peek is a step in the way of the page that actually answers the
                * question.
                */}
              <button type="button" className="fab-icon nodes-details" onClick={() => openProfile(subject.id)} aria-label={t('nodes.details')} title={t('nodes.details')}>
                <Icon name="info" />
              </button>
            </span>
          ) : null}
        </div>
        {/*
          * Why there is a mark on their circle.
          *
          * A gold heart appears on Sarah's circle and on Tom's on their
          * anniversary, and on the tree that is all it can say. Here there is
          * room for the sentence, so it is said: the same icon, what is
          * happening and when, tucked under the way out in the colour of the
          * day. Only when there is something to explain — nothing is held in
          * reserve for it, and a circle with nothing on it shows nothing here.
          */}
        {marks.length > 0 && !flying && (
          <div className="nodes-marks">
            {marks.map((m) => (
              <p key={m.key} className={`nodes-mark is-${m.tone}`}>
                <Icon name={m.icon} />
                <span>{m.text}</span>
              </p>
            ))}
          </div>
        )}
        {/* Only while the screen has been moved off the middle: the way back. */}
        {moved && !flying && (
          <button type="button" className="fab-icon nodes-home" onClick={recentre} aria-label={t('memories.recentre')} title={t('memories.recentre')}>
            <Icon name="center" />
          </button>
        )}

        <div ref={spaceRef} className={`nodes-space ${flying ? 'is-flying' : ''} ${gliding ? 'is-gliding' : ''}`} style={spaceStyle}>
          {/* The lines are drawn from nothing at the centre, so they can spill outside their own box. */}
          <svg className="nodes-links" width="1" height="1" aria-hidden="true">
            {/* A thread for each node that is there to be tied — the rest of the ring is not drawn, and neither are its threads. */}
            {ring.map((p, i) => (isNear(i) ? <line key={ringItems[i].key} x1="0" y1="0" x2={p.x} y2={p.y} /> : null))}
          </svg>

          <div className="nodes-hub-slot">
            {(() => {
              /*
               * A face or a photograph fills the node; with neither — a simcha
               * nobody has put a picture on yet — the mark of what kind of day
               * it is *is* the node, large in the middle, rather than a ring
               * around nothing. Nothing is stuck on the rim: the coloured ring
               * already says which kind of day this is and the words are at the
               * top of the screen.
               *
               * A story with a voice on it wears a play button instead, because
               * that is what tapping it leads to.
               */
              const inner = (
                <>
                  <span className="nodes-hub-fill">
                    {head.photo ? (
                      /* The card-sized copy: the middle node is a hundred and sixty pixels across, not sixteen hundred. */
                      <PhotoImage photo={head.photo} className="nodes-hub-photo" thumb="medium" />
                    ) : head.person ? (
                      <Avatar person={head.person} lang={lang} size="lg" />
                    ) : (
                      <span className="nodes-hub-icon" aria-hidden="true"><Icon name={head.icon} /></span>
                    )}
                    {head.play && <span className="nodes-hub-play" aria-hidden="true"><Icon name="play" /></span>}
                  </span>
                </>
              );
              // A story and a birth have somewhere further to go; a simcha is already all of itself.
              return head.onOpen ? (
                <button type="button" className={`nodes-hub is-${head.tone} is-tappable`} style={hubStyle} onClick={head.onOpen} aria-label={head.open ?? head.title} title={head.open ?? head.title}>
                  {inner}
                </button>
              ) : (
                <div className={`nodes-hub is-${head.tone}`} style={hubStyle} role="img" aria-label={head.title}>{inner}</div>
              );
            })()}
            {/*
              * The camera, sitting on the bottom corner of the rim — half on the
              * face, half off it. **This is where a profile picture is changed
              * now.** You open your own circle and the one control that is about
              * *you* rather than about your pictures is right there on the edge
              * of your own face, which is the only place anybody looked for it.
              *
              * A sibling of the node rather than a child of it, because for a
              * story or a birth the node is itself a button and a button may not
              * contain one. It is kept out of the flight (the node travels, this
              * does not) and simply arrives with the rest of the screen.
              */}
            {isPerson && mayChangePortrait && head.person && (
              <HubCamera person={head.person} adminOnly={photoRight === 'admin'} hidden={flying} />
            )}
          </div>

          {/*
            * Three nested boxes on purpose, because three different things move
            * it: the outer one is *where* it sits (an inline translate), the
            * middle one is its arrival (a transition), and the button itself
            * drifts for ever (an animation). One element cannot hold three
            * transforms without them fighting.
            */}
          {ringItems.map((node, i) =>
            isNear(i) ? (
              <RingNodeView
                key={node.key}
                node={node}
                x={ring[i].x}
                y={ring[i].y}
                size={sizes[i]}
                /*
                 * They arrive one after another, nearest first — but only so far:
                 * the thirtieth and the three-hundredth come in together. The
                 * delay used to keep counting, so the far side of a big ring was
                 * still "arriving" ten seconds after the screen opened.
                 */
                delay={flying ? 0 : 90 + Math.min(i, ARRIVAL_STEPS) * 26}
                floats={i < FLOATING}
                drift={i % 5}
                fresh={isPerson && ageInDays(node.at, now) < FRESH_DAYS / 5}
                label={nodeLabel(node, t)}
                title={node.kind === 'recipe' ? node.recipe.doc.title || t('recipe.untitled') : node.kind === 'story' ? node.story.title || t('story.untitled') : ''}
                onOpen={openNode}
                onHold={startPress}
                onLetGo={cancelPress}
                onMenu={offerAsFace}
              />
            ) : null,
          )}

        </div>

        {/*
          * A circle with nothing around it. Said once, quietly, under the face —
          * a bare node in the middle of an empty screen looks like a page that
          * failed to load rather than a life nobody has added to yet.
          */}
        {ringItems.length === 0 && !flying && (
          <div className="nodes-empty">
            <p>{t('nodes.nothing')}</p>
            {subject.kind === 'person' && <p className="small muted">{t('nodes.nothingHint')}</p>}
            {isRecipe && <p className="small muted">{t('recipe.pictures.invite')}</p>}
          </div>
        )}

        {/*
          * The way out, as a corner rather than a button.
          *
          * Jay: *"can we have the back button on the bottom left … this is more
          * mobile friendly"*, and then: *"a wedge taken away from the bottom
          * left corner with a back icon on it … so it looks less like a button
          * you click and more like a way to go back."*
          *
          * So it is the corner of the screen cut off on a straight diagonal —
          * the biggest target in the app, right where a thumb rests, and a shape
          * rather than a control. It lost its arrival pulse with the move: a
          * hundred pixels of teal in the corner does not need introducing.
          */}
        <button type="button" className={`back-wedge nodes-back ${flying ? 'is-flying' : ''}`} onClick={close} aria-label={t('nav.back')} title={t('nav.back')}>
          <span className="back-wedge-mark" aria-hidden="true"><Icon name="back" className="icon-mirror" /></span>
        </button>

        {/* One thing to do here, and it is the whole point of the screen. */}
        {session && (
          <button type="button" className={`nodes-add ${flying ? 'is-flying' : ''}`} onClick={() => setAdding(true)} aria-label={t('memories.add')} title={t('memories.add')}>
            <Icon name="plus" />
          </button>
        )}
      </div>

      {/*
        * Everything you can add to a day, in one place — which is why the story
        * panel no longer carries its own buttons.
        *
        * There is no second way to speak here. A story is one telling, start to
        * finish, and anything else anyone wants to say about the day is **Say
        * something about this day** — the same voice note a wedding or a birth
        * takes, arriving as another node on the ring beside the photographs
        * rather than as a "Part 2" buried inside somebody else's recording.
        * Only correcting the story is particular to a story, so only that is
        * kept behind the kind.
        */}
      <Dialog open={adding} onClose={() => setAdding(false)} title={subject.kind === 'person' ? t('nodes.addToPerson', { name: head.title }) : t('memories.add')} labelledBy="add-to-day-title">
        <div className="stack">
          <AddMemories subject={subject} className="mb-0" />
          {anchor ? (
            <>
              <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => { setAdding(false); setRecording(true); }}>
                <Icon name="mic" />
                <span>{isRecipe ? t('voice.title.recipe') : t('voice.title')}</span>
              </button>
              {anchor.kind === 'story' && (
                <button type="button" className="btn btn-ghost btn-lg btn-block" onClick={() => { setAdding(false); openStory(anchor.id, { intent: 'edit' }); }}>
                  <Icon name="edit" />
                  <span>{t('story.suggest')}</span>
                </button>
              )}
            </>
          ) : (
            /* A person's own ring: a story is the thing worth adding to it, and the recorder
               works out whose voice it may record (their own, or someone they look after). */
            <>
              <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => { setAdding(false); onClose(); navigate(`/record?speaker=${subject.id}`); }}>
                <Icon name="mic" />
                <span>{t('nodes.recordStory')}</span>
              </button>
              {/*
                * And a recipe — theirs. Pressed on Savta's circle this writes down
                * *Savta's* recipe, whoever is holding the phone, which is how most of
                * the recipes worth keeping will get here. The editor opens over this
                * circle, so the new node arrives on the ring you were looking at.
                */}
              <button type="button" className="btn btn-secondary btn-lg btn-block nodes-add-recipe" onClick={() => { setAdding(false); openRecipeEditor({ personId: subject.id }); }}>
                <Icon name="recipe" />
                <span>{t('recipe.add')}</span>
              </button>
            </>
          )}
        </div>
      </Dialog>

      {anchor && <VoiceNoteDialog anchor={anchor} open={recording} onClose={() => setRecording(false)} label={isRecipe ? t('voice.title.recipe') : undefined} />}
      <PortraitDialog person={head.person} portrait={openPortrait} onClose={() => setOpenPortrait(null)} />

      {/* Held down long enough to mean it: the offer to put this picture on the tree. */}
      <UsePhotoDialog node={pickFrom} person={isPerson ? head.person : undefined} onClose={() => setPickFrom(null)} />

      <MomentDialog photo={openItem} inside={!!anchor && !!openItem?.addedTo && sameAnchor(openItem.addedTo, anchor)} onClose={() => setOpenItem(null)} onChange={setOpenItem} />
    </>
  );
}

/**
 * One thing floating around the middle.
 *
 * A day holds only pictures, clips and voice notes. A person holds more than
 * that: the same pictures, plus the stories they are in and the circle photos
 * the family has chosen for them over the years — because all three are
 * "something of theirs", and splitting them across three screens is what the
 * old card did.
 */
type RingBase = {
  key: string;
  /**
   * When it arrived — published, uploaded, chosen. Two things are read off it:
   * the order (newest nearest the middle) and how new it still is (how much
   * bigger it is drawn, on the same clock as the badges in the tree).
   *
   * Arrival rather than the day it records, on purpose: a box of 1950s
   * photographs scanned this morning *is* the latest thing on somebody's
   * circle, and it should be near the face and large — which is exactly what
   * their circle in the tree has been saying all week.
   */
  at: string;
  /**
   * Theirs because they put it there, rather than because they are in it.
   * A person's own circle draws these larger: at a glance you can see what
   * somebody has given the family against what the family has of them.
   */
  posted?: boolean;
};
type RingNode = RingBase &
  (
    | { kind: 'photo'; photo: FamilyPhoto }
    | { kind: 'day'; day: FamilyPhoto }
    | { kind: 'story'; story: Story; hasVoice: boolean }
    | { kind: 'recipe'; recipe: Recipe }
    | { kind: 'portrait'; portrait: PersonPortrait }
  );

/**
 * One node on the ring.
 *
 * A component of its own, and memoised, because the screen around it re-renders
 * for reasons that have nothing to do with any one node — a picture opened, the
 * window moved along the ring, somebody in the family adding something — and a
 * ring can be hundreds of these. With everything it needs handed in as plain
 * values and four callbacks that never change, a node is only ever rendered
 * again when something about *it* did.
 */
const RingNodeView = memo(function RingNodeView({
  node, x, y, size, delay, floats, drift, fresh, label, title, onOpen, onHold, onLetGo, onMenu,
}: {
  node: RingNode;
  x: number;
  y: number;
  size: number;
  /** How long after the screen opens this one arrives, in ms. */
  delay: number;
  /** Whether it drifts. Only the nearest few do — see FLOATING. */
  floats: boolean;
  /** Which of five tempos it drifts on, so neighbours never move in step. */
  drift: number;
  fresh: boolean;
  label: string;
  /** The words under the mark, for a story or a recipe (a day's are its label). */
  title: string;
  onOpen: (node: RingNode, el: HTMLElement) => void;
  onHold: (node: RingNode) => void;
  onLetGo: () => void;
  onMenu: (node: RingNode, e: ReactMouseEvent) => void;
}) {
  return (
    <div className="nodes-sub" data-node={node.key} style={{ transform: `translate(${x}px, ${y}px)`, '--node': `${size}px` } as CSSProperties}>
      <span className="nodes-sub-in" style={{ transitionDelay: `${delay}ms` }}>
        <button
          type="button"
          className={`nodes-sub-btn ${node.posted ? 'is-posted' : ''} ${fresh ? 'is-fresh' : ''} ${node.kind === 'photo' && isAudio(node.photo) ? 'is-voice' : ''} ${node.kind === 'story' ? 'is-story' : ''} ${node.kind === 'recipe' ? `is-recipe ${node.recipe.status === 'draft' ? 'is-draft' : ''}` : ''} ${node.kind === 'portrait' ? 'is-portrait' : ''} ${node.kind === 'day' ? `is-day is-${node.day.milestone ? MILESTONE_TONES[node.day.milestone] : 'grey'}` : ''}`}
          /*
            * The drift is life, and a hundred of them is a phone's
            * whole frame budget — every one is its own infinitely
            * animated transform. So only the nodes you are actually
            * looking at breathe; the rest are still, which nobody
            * notices because they are a ring or two out.
            */
          style={floats ? { animationDelay: `${drift * -1.7}s` } : { animation: 'none' }}
          /* Hold it down — or right-click it — to make it the face on the tree. */
          onPointerDown={() => onHold(node)}
          onPointerUp={onLetGo}
          onPointerCancel={onLetGo}
          onPointerLeave={onLetGo}
          onContextMenu={(e) => onMenu(node, e)}
          onClick={(e) => onOpen(node, e.currentTarget)}
          aria-label={label}
          title={label}
        >
          {node.kind === 'day' ? (
            /* A day of theirs wears the mark of what kind of day it is — the same icon as on the timeline. */
            <span className="nodes-story">
              <Icon name={node.day.milestone ? MILESTONE_ICONS[node.day.milestone] : 'milestone'} />
              <span className="nodes-story-title">{label}</span>
            </span>
          ) : node.kind === 'recipe' ? (
            /*
             * A recipe wears the cook's hat, in the kitchen's own colour, with the
             * dish's name under it — so on a ring of faces and photographs it is
             * recognised as *something to cook* before a word of it is read. It is
             * the only node that wears either.
             */
            <span className="nodes-story">
              <Icon name="recipe" />
              <span className="nodes-story-title" dir="auto">{title}</span>
            </span>
          ) : node.kind === 'story' ? (
            /* A story is a voice, so it wears the same mark it wears in the middle of its own day. */
            <span className="nodes-story">
              <Icon name={node.hasVoice ? 'play' : 'stories'} />
              <span className="nodes-story-title">{title}</span>
            </span>
          ) : node.kind === 'portrait' ? (
            <PortraitThumb portrait={node.portrait} />
          ) : isAudio(node.photo) ? (
            <span className="nodes-voice">
              <Icon name="mic" />
              {node.photo.durationMs ? <span className="nodes-voice-time">{formatDuration(node.photo.durationMs)}</span> : null}
            </span>
          ) : (
            <PhotoImage photo={node.photo} className="nodes-sub-photo" thumb />
          )}
          {node.kind === 'photo' && isVideo(node.photo) && <span className="nodes-sub-mark" aria-hidden="true"><Icon name="play" /></span>}
          {node.kind === 'portrait' && node.portrait.current && <span className="nodes-sub-mark is-current" aria-hidden="true"><Icon name="check" /></span>}
          {/* Still being written — and, since only its writer is ever shown it, theirs to finish. */}
          {node.kind === 'recipe' && node.recipe.status === 'draft' && <span className="nodes-sub-mark is-draft" aria-hidden="true"><Icon name="edit" /></span>}
        </button>
      </span>
    </div>
  );
});

/** What a node is called, for anyone listening rather than looking. */
function nodeLabel(node: RingNode, t: ReturnType<typeof useI18n>['t']): string {
  if (node.kind === 'story') return node.story.title || t('story.untitled');
  if (node.kind === 'recipe') return `${t(node.recipe.status === 'draft' ? 'recipe.draft' : 'recipe.one')}: ${node.recipe.doc.title || t('recipe.untitled')}`;
  if (node.kind === 'day') return node.day.title || (node.day.milestone ? t(`milestone.${node.day.milestone}` as 'milestone.wedding') : t('photos.eventMark'));
  if (node.kind === 'portrait') return node.portrait.current ? t('portrait.current') : t('tree.badge.photo');
  const m = node.photo;
  return m.caption || (isAudio(m) ? t('voice.note') : isVideo(m) ? t('memories.video') : t('memories.photo'));
}

/**
 * Everything that belongs on the ring, newest nearest.
 *
 * The order *is* the layout: `layoutRings` fills the innermost ring first, so
 * sorting newest-first puts this year's pictures around the face and the old
 * ones further out — which is how anyone would arrange them on a table.
 */
function useRingItems(subject: MemorySubject, items: FamilyPhoto[]): RingNode[] {
  const { stories, contributions, storyPeople, moments, links, recipes } = useApp();
  const repos = useRepos();
  const [portraits, setPortraits] = useState<PersonPortrait[]>([]);
  const personId = subject.kind === 'person' ? subject.id : null;

  useEffect(() => {
    if (!personId) {
      setPortraits([]);
      return undefined;
    }
    let cancelled = false;
    void repos.people.listPortraits(personId).then((list) => {
      if (!cancelled) setPortraits(list);
    });
    return () => {
      cancelled = true;
    };
  }, [repos, personId]);

  return useMemo(() => {
    if (!personId) return items.map((m): RingNode => ({ key: `photo:${m.id}`, at: m.createdAt, kind: 'photo', photo: m }));
    /*
     * Everything of theirs, in one sweep — which is two different things and
     * the difference is worth seeing:
     *
     *   - **what they gave the family**: every picture, clip and recording
     *     uploaded by an account belonging to their circle, whether or not
     *     anybody tagged them in it. Drawn larger.
     *   - **what the family has of them**: everything their face is tagged on,
     *     the stories they told or are in, their own days, the portraits
     *     chosen for them. Drawn smaller.
     *
     * Their own days — a bar mitzvah, a wedding — are nodes in their own right
     * rather than pictures: on Lior's circle the bar mitzvah *is* the thing,
     * and it belongs there whether or not anyone has put a photograph in it
     * yet. Tapping one opens that day's own constellation, so the two shapes
     * nest: a person, a day of theirs, the pictures inside it.
     */
    /*
     * Read out of piles, not looked for (lib/grouped): what this account
     * shared, what this person is pinned to, the stories they told, are in, or
     * spoke on. Every one of these used to be a walk over everything the family
     * has — and the stories were walked once per recording on top of that — which
     * is nothing for a demo and a long pause on a phone for a real family, every
     * time a circle is opened and again whenever anybody adds anything.
     */
    const mine = new Set(links.filter((l) => l.personId === personId && l.status === 'active').map((l) => l.accountId));
    const seen = new Set<string>();
    const theirs: RingNode[] = [];
    const sharedBy = groupedBy(moments, momentUploader);
    const pinnedTo = pileOf(groupedByEach(moments, momentPeople), personId);
    const recordingsOn = groupedBy(contributions, contributionStory);
    const addPhoto = (m: FamilyPhoto, posted: boolean) => {
      if (seen.has(m.id)) return;
      seen.add(m.id);
      if (isOwnDay(m)) theirs.push({ key: `day:${m.id}`, at: m.createdAt, kind: 'day', day: m, posted });
      else theirs.push({ key: `photo:${m.id}`, at: m.createdAt, kind: 'photo', photo: m, posted });
    };
    /*
     * Uploaded by them first, so a picture that is both theirs and of them
     * counts as theirs. A day they created counts too even with no picture in
     * it yet — putting a simcha on the family's timeline is a thing they gave.
     */
    for (const account of mine) for (const m of pileOf(sharedBy, account)) if (!m.removed && (!!m.storageKey || isOwnDay(m))) addPhoto(m, true);
    for (const m of items) addPhoto(m, false);
    for (const m of pinnedTo) if (!m.removed && isOwnDay(m)) addPhoto(m, false);
    /*
     * Every story that is theirs, told by them *or about them*. Saba Moshe
     * never recorded anything; three of the family's stories are about him, and
     * on his circle those are exactly what anyone has come to hear.
     */
    const storyOf = byId(stories);
    const spokeOn = new Set(pileOf(groupedBy(contributions, contributionSpeaker), personId).filter((c) => c.status === 'published').map((c) => c.storyId));
    const theirStories = new Set<UUID>([
      ...pileOf(groupedBy(stories, storyTeller), personId).map((s) => s.id),
      ...pileOf(groupedBy(storyPeople, storyPersonPerson), personId).map((sp) => sp.storyId),
      ...spokeOn,
    ]);
    for (const id of theirStories) {
      const s = storyOf.get(id);
      if (!s || s.status !== 'published') continue;
      const hasVoice = pileOf(recordingsOn, s.id).some((c) => c.kind === 'audio' && c.status === 'published');
      // A story they recorded is theirs; one they are only named in belongs to whoever told it.
      const posted = s.primaryStorytellerPersonId === personId || spokeOn.has(s.id);
      theirs.push({ key: `story:${s.id}`, at: s.publishedAt ?? s.updatedAt, kind: 'story', story: s, hasVoice, posted });
    }
    /*
     * Their recipes — and this ring is the only place in the app a recipe is
     * found. Both kinds count as something they gave the family: the ones that
     * are theirs, and the ones they sat down and wrote out for somebody else. A
     * draft is in the list only for its own writer (the repository never hands it
     * to anyone else), so it shows here, dashed, as the way back to finishing it.
     */
    for (const r of recipesOnCircle(recipes, personId, mine)) theirs.push({ key: `recipe:${r.id}`, at: recipeWhen(r), kind: 'recipe', recipe: r, posted: true });
    for (const p of portraits) theirs.push({ key: `portrait:${p.id}`, at: p.createdAt, kind: 'portrait', portrait: p });
    return theirs.sort((a, b) => b.at.localeCompare(a.at));
  }, [items, personId, moments, links, stories, contributions, storyPeople, portraits, recipes]);
}

/** A circle photo on the ring. */
function PortraitThumb({ portrait }: { portrait: PersonPortrait }) {
  // A circle on the ring is 76px at its biggest: the small copy, never the 640px original.
  const { src, onError, onLoad } = usePortraitImg(`portrait:${portrait.id}`, true);
  return src ? <img className="nodes-sub-photo" src={src} alt="" draggable={false} decoding="async" onError={onError} onLoad={onLoad} /> : <span className="nodes-sub-photo is-empty" />;
}

/**
 * One of the family's pictures of somebody, full size — and, for whoever looks
 * after that circle, the way to make it the face on the tree. This used to live
 * at the bottom of the person card; it belongs here now, where the pictures do.
 */
function PortraitDialog({ person, portrait, onClose }: { person: PersonWithNames | undefined; portrait: PersonPortrait | null; onClose: () => void }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { actor, links, grants, reload } = useApp();
  const { src: url, onError: onPortraitError, onLoad: onPortraitLoad } = usePortraitImg(portrait ? `portrait:${portrait.id}` : '');
  const mayChoose = !!person && !!actor && circlePortraitRight(actor, links, grants, person) !== 'none';
  const choose = async () => {
    if (!person || !portrait) return;
    const res = await repos.people.choosePortrait(person.id, portrait.id);
    if (res.ok) {
      toast(t('portrait.current'));
      onClose();
      reload();
    } else toast(t('error.unexpected'));
  };
  return (
    <Dialog
      open={!!portrait}
      onClose={onClose}
      title={person ? displayName(person, lang) : t('tree.badge.photo')}
      labelledBy="nodes-portrait-title"
      wide
      actions={
        portrait && mayChoose && !portrait.current ? (
          <button type="button" className="btn btn-primary" onClick={() => void choose()}>
            <Icon name="check" />
            <span>{t('portrait.use')}</span>
          </button>
        ) : undefined
      }
    >
      {portrait && (
        <div className="stack">
          {url ? <img className="photo-full" src={url} alt="" draggable={false} onError={onPortraitError} onLoad={onPortraitLoad} /> : <span className="photo-placeholder photo-full" />}
          <div className="small muted">
            {portrait.current ? <span className="chip chip-info xs" style={{ marginInlineEnd: 8 }}>{t('portrait.current')}</span> : null}
            {t('portrait.addedWhen', { when: relativeTime(portrait.createdAt, lang) })}
          </div>
        </div>
      )}
    </Dialog>
  );
}

/**
 * The camera on the rim of the middle node — the one place a profile picture is
 * changed.
 *
 * It asks its two questions (take one, or choose one) in a sheet rather than in
 * the little anchored menu the person card uses: this is a 34px disc sitting on
 * the edge of a circle in the middle of a full-screen overlay, and a menu hung
 * off it would fall outside the picture. Everything underneath is the person
 * card's own flow (`usePortraitUpload`) — the hidden inputs, the webcam dialog,
 * the crop-and-save — so a photograph taken here is a photograph taken there.
 */
function HubCamera({ person, adminOnly, hidden }: { person: PersonWithNames; adminOnly: boolean; hidden: boolean }) {
  const { t } = useI18n();
  const { busy, elements, takePhoto, choosePhoto } = usePortraitUpload(person);
  const [asking, setAsking] = useState(false);
  const label = adminOnly ? t('portrait.adminChange') : person.portraitUrl ? t('portrait.change') : t('portrait.add');
  return (
    <>
      {!hidden && (
        <button
          type="button"
          className={`nodes-hub-camera ${adminOnly ? 'is-admin-control' : ''}`}
          onClick={() => setAsking(true)}
          disabled={busy}
          aria-label={label}
          title={label}
        >
          <Icon name={busy ? 'refresh' : 'camera'} />
        </button>
      )}
      <Dialog open={asking} onClose={() => setAsking(false)} title={label} labelledBy="hub-camera-title">
        <div className="stack">
          <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => { setAsking(false); takePhoto(); }}>
            <Icon name="camera" />
            <span>{t('portrait.take')}</span>
          </button>
          <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => { setAsking(false); choosePhoto(); }}>
            <Icon name="image" />
            <span>{t('portrait.choose')}</span>
          </button>
        </div>
      </Dialog>
      {elements}
    </>
  );
}

/**
 * "Show this photo in the tree", offered by holding a picture on the ring down.
 *
 * Two different things happen underneath, and the difference matters:
 *
 *   - a **portrait** is one of the faces the family has already chosen for this
 *     circle, so it is simply made the current one;
 *   - a **picture** is an ordinary Moment — a photograph of them at somebody's
 *     wedding. It cannot be "chosen", because it is not one of their faces yet.
 *     Its blob is read back, cropped square the way every portrait is, and added
 *     as a new photo of theirs. The Moment itself is untouched and stays exactly
 *     where it was, and the gallery keeps every face the family has ever picked.
 */
function UsePhotoDialog({ node, person, onClose }: { node: RingNode | null; person: PersonWithNames | undefined; onClose: () => void }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { reload } = useApp();
  const [busy, setBusy] = useState(false);
  const already = node?.kind === 'portrait' && node.portrait.current;

  const apply = async () => {
    if (!node || !person) return;
    setBusy(true);
    try {
      if (node.kind === 'portrait') {
        const res = await repos.people.choosePortrait(person.id, node.portrait.id);
        if (!res.ok) throw new Error('refused');
        toast(t('portrait.current'));
      } else if (node.kind === 'photo') {
        // A blob url is the one shape both the prototype and a real backend can hand over.
        const url = await repos.photos.getUrl(node.photo.id);
        if (!url) throw new Error('gone');
        const prepared = await preparePortrait(await (await fetch(url)).blob());
        const res = await repos.people.addPortrait(person.id, prepared.blob, { width: prepared.width, height: prepared.height }, uuid());
        if (!res.ok) throw new Error('refused');
        toast(t('portrait.saved'));
      }
      reload();
      onClose();
    } catch {
      toast(t('portrait.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!node && !!person}
      onClose={onClose}
      title={person ? displayName(person, lang) : t('portrait.use')}
      labelledBy="use-photo-title"
      actions={
        already ? undefined : (
          <button type="button" className="btn btn-primary" onClick={() => void apply()} disabled={busy}>
            <Icon name="check" />
            <span>{t('portrait.use')}</span>
          </button>
        )
      }
    >
      {node && (
        <div className="stack">
          {/* Round, because that is the shape it will take — the square corners of the original are not the offer. */}
          <span className="use-photo-preview">
            {node.kind === 'portrait' ? <PortraitThumb portrait={node.portrait} /> : node.kind === 'photo' ? <PhotoImage photo={node.photo} thumb /> : null}
          </span>
          {/* Only when there is nothing to press: the button below says the rest. */}
          {already && <p className="small muted center">{t('portrait.current')}</p>}
        </div>
      )}
    </Dialog>
  );
}

/**
 * What is happening to this person today, for the marks their circle wears in
 * the tree.
 *
 * The circle can only show a gold heart; this turns it back into a sentence.
 * Today's day comes first (that *is* the mark); with nothing today the soonest
 * one in the coming weeks is shown instead, and the date says which it is, so
 * the line is never mistaken for something happening now. Shabbat is its own
 * line, because the candles on the rim are their own mark.
 */
function useMarks(subject: MemorySubject): Array<{ key: string; icon: string; tone: string; text: string }> {
  const { t, lang } = useI18n();
  const { occasionToday, occasions, shabbatIds, peopleById, me } = useApp();
  const id = subject.kind === 'person' || subject.kind === 'birth' ? subject.id : null;
  return useMemo(() => {
    if (!id) return [];
    const out: Array<{ key: string; icon: string; tone: string; text: string }> = [];
    const o = occasionToday.get(id) ?? nextOccasionFor(occasions, id);
    if (o) {
      const when = occasionDayLabel(o, lang, { today: t('occasion.today'), tomorrow: t('occasion.tomorrow') });
      out.push({ key: 'occasion', icon: occasionIcon(o.kind), tone: o.kind, text: `${occasionText(o, lang, t, peopleById, id)} · ${when}` });
    }
    const p = peopleById.get(id);
    // A circle whose person has died wears ז״ל, never candles — see PersonBubble.
    if (shabbatIds.has(id) && p && p.status !== 'deceased') {
      // On your own circle, "where Sarah is" is a funny way to say "here".
      out.push({ key: 'shabbat', icon: 'candles', tone: 'shabbat', text: me?.id === id ? t('shabbat.now') : t('shabbat.nowFor', { name: givenName(p, lang) }) });
    }
    return out;
  }, [id, occasionToday, occasions, shabbatIds, peopleById, me, lang, t]);
}

/** What the middle node is: its picture, its colour, its name, and where tapping it goes. */
function useHead(subject: MemorySubject) {
  const { t, lang } = useI18n();
  const { moments, stories, recipes, peopleById, contributions, openStory, openRecipe, openRecipeEditor, openPersonCard } = useApp();
  return useMemo((): null | {
    title: string;
    kind: string;
    when: string;
    icon: string;
    tone: MilestoneTone | 'recipe';
    photo?: FamilyPhoto;
    person?: PersonWithNames;
    onOpen?: () => void;
    open?: string;
    /** A voice to listen to: the node wears a play button. */
    play?: boolean;
  } => {
    const anchor = subject;
    if (anchor.kind === 'person') {
      const p = peopleById.get(anchor.id);
      if (!p) return null;
      return {
        title: displayName(p, lang),
        // Their years, which is what a face on a tree cannot tell you.
        kind: lifeYears(p.birth, p.death, p.status === 'deceased', lang) || '',
        when: '',
        icon: 'person',
        tone: 'teal' as MilestoneTone,
        person: p,
        // Not a button. Their details are behind **Details** at the top; nothing here opens a card by itself.
      };
    }
    if (anchor.kind === 'moment') {
      const m = byId(moments).get(anchor.id);
      if (!m) return null;
      const kindLabel = m.milestone ? t(`milestone.${m.milestone}` as 'milestone.wedding') : t('photos.eventMark');
      return {
        title: m.title || kindLabel,
        kind: kindLabel,
        when: m.takenOn ? formatFuzzyDate(m.takenOn, lang) : '',
        icon: m.milestone ? MILESTONE_ICONS[m.milestone] : 'milestone',
        tone: m.milestone ? MILESTONE_TONES[m.milestone] : 'grey',
        // Its main picture (domain/covers.ts): the one somebody chose, else the first — which, for a simcha made
        // with a picture, is its own. A day whose only media is a clip still wears the clip, as it always did.
        photo: coverOf(anchor, moments) ?? (m.storageKey ? m : undefined),
      };
    }
    if (anchor.kind === 'story') {
      const s = byId(stories).get(anchor.id);
      if (!s) return null;
      const teller = peopleById.get(s.primaryStorytellerPersonId);
      const hasVoice = pileOf(groupedBy(contributions, contributionStory), s.id).some((c) => c.kind === 'audio' && c.status === 'published');
      return {
        title: s.title || t('story.untitled'),
        kind: teller ? t('story.toldBy', { name: displayName(teller, lang) }) : t('stories.title'),
        when: formatFuzzyDate(s.date, lang),
        icon: 'mic',
        tone: 'teal',
        // A story wears its main picture once the family has given it one; until then, the face of whoever tells it.
        photo: coverOf(anchor, moments),
        person: teller,
        play: hasVoice,
        onOpen: () => openStory(s.id, { intent: hasVoice ? 'play' : undefined }),
        open: t('memories.openStory'),
      };
    }
    /*
     * A recipe, which is the one thing here that is not a day: the page the
     * family cooks from in the middle, and everything they have added to it —
     * their photographs of the dish, somebody saying what their mother did
     * differently — around it. Tapping the middle opens the page itself, the way
     * tapping a story opens the story.
     */
    if (anchor.kind === 'recipe') {
      const r = byId(recipes).get(anchor.id);
      if (!r) return null;
      const cook = peopleById.get(r.personId);
      const draft = r.status === 'draft';
      return {
        title: r.doc.title || t('recipe.untitled'),
        kind: cook ? t('recipe.byline.of', { name: displayName(cook, lang) }) : t('passed.kind.recipes'),
        when: draft ? t('recipe.draft') : r.publishedAt ? relativeTime(r.publishedAt, lang) : '',
        icon: 'recipe',
        tone: 'recipe' as const,
        // The picture that stands for it fills the node, exactly as a simcha's own photograph does.
        photo: recipeCover(r, moments),
        onOpen: () => (draft ? openRecipeEditor({ recipeId: r.id }) : openRecipe(r.id)),
        open: t('memories.openRecipe'),
      };
    }
    /*
     * The day somebody arrived. The middle node is not a button: tapping a
     * person used to throw a card of details over the screen, which is the
     * thing this whole shape replaced. Their details are behind **Details** at
     * the top, like they are on their own circle.
     */
    const p = peopleById.get(anchor.id);
    if (!p) return null;
    return {
      title: t('memories.birthTitle', { name: displayName(p, lang) }),
      kind: t('memories.birthKind'),
      when: formatFuzzyDate(p.birth, lang),
      icon: 'cake',
      tone: 'green',
      // The baby picture the family chose, when there is one; their face today when there is not.
      photo: coverOf(anchor, moments),
      person: p,
    };
  }, [subject, moments, stories, recipes, peopleById, contributions, t, lang, openStory, openRecipe, openRecipeEditor, openPersonCard]);
}

/**
 * Every place, ring by ring outwards.
 *
 * A ring at a time, because a day's things should look ordered rather than
 * scattered: everything on the first ring is the same distance from the middle,
 * and only when that ring is full does the next one start, a node's width
 * further out and turned half a step so nothing lines up into spokes. A ring
 * that is not full spreads its few evenly around the whole circle instead of
 * leaving a gap.
 *
 * **Nothing is ever shrunk to make room.** There is no last ring: a family that
 * drops two hundred wedding photographs into a day gets two hundred places,
 * every one of them full size, and the constellation simply grows past the
 * screen — the screen then moves over it (see the panning in the component).
 */
const NODE = 64;
const GAP = 16;
/** A person's own circle: what they gave the family, and what the family has of them. */
const NODE_POSTED = 76;
const NODE_TAGGED = 52;
/** How many nodes drift. Past this they sit still — see the note where it is used. */
const FLOATING = 24;
/** A ring with more on it than this is mounted through a window rather than whole — see `around` in the component. */
const WINDOW_FROM = 60;
/**
 * How far past the edge of the screen stays mounted, as a share of the screen.
 * A quarter is enough that a drag never outruns the nodes (the box is looked at
 * every frame) and no more than that on purpose: a person's ring is packed at
 * forty-pixel circles, so every extra share of a screen is another hundred
 * buttons with a picture in each.
 */
const WINDOW_MARGIN = 0.25;
/** Nodes arrive one after another for this many steps; everything after that comes in with the last of them. */
const ARRIVAL_STEPS = 30;
/** The biggest a node is ever drawn, at 1:1 — one given to the family, on the day it arrived. */
const NODE_REACH = NODE_POSTED * FRESH_PEAK;
/**
 * How big one node is drawn.
 *
 * A day's things are all the same: they are all "in this day" and nothing else.
 * A person's are not — theirs is bigger than one they are only in — and they
 * also stretch back across a life, so the further out a node sits the further
 * back it is and the smaller it is drawn. That is not decoration: it is what
 * keeps a thousand-picture circle legible, and it means the recent and the
 * given are the ones your eye lands on. Never below 40px, which is still a
 * thumb at 1:1 and the screen zooms anyway.
 */
function nodeSize(node: RingNode, index: number, person: boolean, now: number, fadeDays: number): number {
  if (!person) return NODE;
  const base = node.posted ? NODE_POSTED : NODE_TAGGED;
  // How crowded it is out there: a ring or two deep the circles step down so a
  // life's worth of pictures stays legible, and cheap.
  const crowd = index < 18 ? 1 : index < 48 ? 0.84 : 0.68;
  // How new it is: up to FRESH_PEAK on arrival, easing back to the size above
  // over the same days the tree's badges take to settle.
  return Math.max(40, Math.round(base * crowd * freshScale(node.at, now, fadeDays)));
}

/**
 * Every place, ring by ring outwards, for nodes that are not all the same size.
 *
 * A ring takes whatever fits around it — each node claiming the arc its own
 * width needs — and then that arc is stretched to the whole circle, so a
 * part-full ring spreads its few evenly instead of leaving a gap, and a big
 * node beside a small one gets the room it actually needs rather than an equal
 * share it would overflow. Each ring is turned half a step so nothing lines up
 * into spokes, and the next ring starts clear of the tallest thing in this one.
 *
 * **Nothing is ever shrunk to make room.** A ring that cannot hold any more
 * starts another one; the constellation simply grows past the screen, and the
 * screen moves over it (see the panning in the component).
 */
function layoutRings(sizes: number[], hub: number): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  let inner = hub / 2 + GAP;
  let turn = 0;
  let i = 0;
  while (i < sizes.length) {
    // How many fit on this ring, growing the radius as a bigger node joins it.
    let count = 0;
    let ringMax = 0;
    let need = 0;
    let r = inner;
    while (i + count < sizes.length) {
      const size = sizes[i + count];
      const max = Math.max(ringMax, size);
      const at = inner + max / 2;
      const arc = need + size + GAP;
      // Three at the least, or a tight inner ring could never close.
      if (count >= 3 && arc > 2 * Math.PI * at) break;
      ringMax = max;
      r = at;
      need = arc;
      count++;
    }
    // Each node centred in a slice as wide as it needs, the slices filling the circle.
    let a = -Math.PI / 2 + turn;
    for (let k = 0; k < count; k++) {
      const share = ((sizes[i + k] + GAP) / need) * Math.PI * 2;
      a += share / 2;
      out.push({ x: Math.round(Math.cos(a) * r), y: Math.round(Math.sin(a) * r) });
      a += share / 2;
    }
    inner += ringMax + GAP;
    turn += Math.PI / count;
    i += count;
  }
  return out;
}

type Space = { w: number; h: number; hFull: number; hub: number };

/** A box in the constellation's own coordinates — the middle node is 0,0. */
type Box = { x0: number; y0: number; x1: number; y1: number };

/** What the screen is showing of the constellation, given where it has been moved to. */
function seenBox(space: Space, v: { x: number; y: number; k: number }): Box {
  const cx = space.w * CENTRE_X + v.x;
  const cy = space.hFull * CENTRE_Y + v.y;
  return { x0: -cx / v.k, y0: -cy / v.k, x1: (space.w - cx) / v.k, y1: (space.hFull - cy) / v.k };
}

/**
 * The same box with room around it, so there is always something already
 * mounted beyond the edge of the screen. Never less room than a node and a half
 * (`reach` is one node, in the constellation's own units): on a phone a quarter
 * of the screen is *narrower* than a node, and a box that cannot even hold the
 * node at its edge is "outgrown" again on every frame of a drag — which is the
 * re-render per frame this window exists to avoid.
 */
function grown(b: Box, reach: number): Box {
  const mx = Math.max((b.x1 - b.x0) * WINDOW_MARGIN, reach * 1.5);
  const my = Math.max((b.y1 - b.y0) * WINDOW_MARGIN, reach * 1.5);
  return { x0: b.x0 - mx, y0: b.y0 - my, x1: b.x1 + mx, y1: b.y1 + my };
}

/** Where the middle of the constellation sits on the screen. CSS is given these, not told them. */
const CENTRE_X = 0.5;
const CENTRE_Y = 0.52;

/**
 * How much room the constellation has. The height the ring is allowed is cut
 * down because the middle sits above centre, with the day's name and the plus
 * below it — the ring has to stay clear of both.
 */
function measure(): Space {
  const w = window.innerWidth;
  const h = window.innerHeight;
  // The band the ring may use: clear of the title at the top and the plus at the foot.
  return { w, h: Math.max(200, h - 260), hFull: h, hub: Math.min(168, Math.max(116, Math.round(Math.min(w, h) * 0.32))) };
}
